import { derivedStats, type DerivedStats } from './combat';
import {canUnequip} from '../../../shared/equipmentCommands.mjs';
import {normalizeShortcutSlots} from '../../../shared/shortcutSlots.mjs';
import equipment from '../../../data/character-equipment.json';
import {
  defaultShortcuts, canAssign, skillById, unlockedSkillsFor, isAdvancedClass,
  DEFAULT_SHORTCUT_KEYS, validShortcutKeys, type ClassId, type Shortcut,
} from './professions';

export type GearSlot='weapon'|'outfit'|'charm';
export type Appearance={
  gender:'male'|'female';
  preset:'phu-bao-thai-ban'|'mae-kha-village';
  face:'weathered'|'angular';
  hair:'short'|'tied';
  skin:'tan'|'deep'|'light';
  outfit:'indigo'|'linen';
};
export type Gear={
  id:string;name:string;slot:GearSlot;classes:ClassId[];kind:string;
  atk:number;hp:number;def:number;tint:number;icon:string;
};
export const GEAR=equipment as Gear[];

export type CharacterState={
  equipment:Record<GearSlot,string>;
  attributes:{power:number;vitality:number;guard:number};
  ranks:number[];
  owned:string[];
  potions:number;
  profession:ClassId;
  learned:Record<string,number>;
  shortcuts:Shortcut[];
  shortcutKeys:string[];
  classVersion:number;
  created:boolean;
  appearance:Appearance;
  unlockedSkills:string[];
  equippedSkills:string[];
  advancementQuest?:boolean;
};

export const defaultAppearance=():Appearance=>({
  gender:'male',preset:'phu-bao-thai-ban',face:'weathered',hair:'short',skin:'tan',outfit:'indigo',
});

export function validAppearance(raw:unknown):raw is Appearance{
  if(!raw||typeof raw!=='object'||Array.isArray(raw))return false;
  const p=raw as Record<string,unknown>;
  const keys=['gender','preset','face','hair','skin','outfit'];
  if(Object.keys(p).length!==keys.length||keys.some(k=>typeof p[k]!=='string'))return false;
  if(!['male','female'].includes(String(p.gender)))return false;
  if(p.preset!==(p.gender==='male'?'phu-bao-thai-ban':'mae-kha-village'))return false;
  return ['weathered','angular'].includes(String(p.face))
    &&['short','tied'].includes(String(p.hair))
    &&['tan','deep','light'].includes(String(p.skin))
    &&['indigo','linen'].includes(String(p.outfit));
}

export function newCharacter():CharacterState{
  return {
    equipment:{weapon:'bamboo-knife',outfit:'indigo',charm:''},
    attributes:{power:0,vitality:0,guard:0},
    ranks:[0,0,0,0],
    owned:['bamboo-knife','indigo','linen','talisman'],
    potions:3,
    profession:'villager',
    learned:{},
    shortcuts:[null,null,null,null],
    shortcutKeys:[...DEFAULT_SHORTCUT_KEYS],
    classVersion:2,
    created:false,
    appearance:defaultAppearance(),
    unlockedSkills:[],
    equippedSkills:[],
    advancementQuest:false,
  };
}

export function createCharacter(state:CharacterState,appearance:Appearance):boolean{
  if(state.created||!validAppearance(appearance))return false;
  state.appearance=structuredClone(appearance);
  state.created=true;
  state.profession='villager';
  state.equipment.weapon='bamboo-knife';
  state.equipment.outfit=appearance.outfit;
  if(!state.owned.includes(appearance.outfit))state.owned.push(appearance.outfit);
  reconcileCharacter(state,1);
  return true;
}

export function attributePoints(state:CharacterState,level:number):number{
  return Math.max(0,(Math.max(1,Math.trunc(level))-1)*3-Object.values(state.attributes).reduce((a,b)=>a+b,0));
}
export function skillPoints(state:CharacterState,level:number):number{
  return Math.max(0,Math.max(1,Math.trunc(level))-1-Math.max(state.ranks.reduce((a,b)=>a+b,0),Object.values(state.learned).reduce((a,b)=>a+b,0)));
}
export function reconcileCharacter(state:CharacterState,level:number):CharacterState{
  const unlocked=unlockedSkillsFor(state.profession,level).map(s=>s.id);
  state.unlockedSkills=unlocked;
  state.shortcuts=normalizeShortcutSlots(state.shortcuts);
  state.equippedSkills=state.shortcuts.flatMap(b=>b?.kind==='skill'&&unlocked.includes(b.id)?[b.id]:[]);
  for(const id of Object.keys(state.learned))if(!unlocked.includes(id))delete state.learned[id];
  const weapon=GEAR.find(g=>g.id===state.equipment.weapon);
  if(!weapon||!weapon.classes.includes(state.profession))state.equipment.weapon='bamboo-knife';
  return state;
}
export function changeProfession(state:CharacterState,id:string,level=1,questComplete=state.advancementQuest===true):boolean{
  if(!state.created||state.profession!=='villager'||level<10||questComplete!==true||!isAdvancedClass(id))return false;
  state.profession=id;
  const grants:Record<string,string[]>={'sword-soldier':['hunter-blade','sword-armor'],'spear-soldier':['thai-spear','spear-armor'],'forest-hunter':['forest-bow','forest-crossbow','hunter-vest'],shaman:['ritual-staff','ritual-talisman','shaman-robe']};
  const granted=grants[id]!;state.owned=[...new Set([...state.owned,...granted])];state.equipment.weapon=granted[0]!;state.equipment.outfit=granted.at(-1)!;
  state.learned={};
  state.ranks=[0,0,0,0];
  const unlocks=unlockedSkillsFor(id,level).map(s=>s.id);
  state.unlockedSkills=unlocks;
  state.shortcuts=defaultShortcuts(id,level);
  state.equippedSkills=state.shortcuts.flatMap(b=>b?.kind==='skill'?[b.id]:[]);
  return true;
}
export function learnSkill(state:CharacterState,level:number,id:string):boolean{
  const skill=skillById(id);
  if(!skill||skill.classId!==state.profession||skill.level>level||!state.unlockedSkills.includes(id)||skillPoints(state,level)<1||(state.learned[id]??0)>=5)return false;
  state.learned[id]=(state.learned[id]??0)+1;return true;
}
export function bindShortcut(state:CharacterState,level:number,slot:number,binding:Shortcut,source?:number):boolean{
  reconcileCharacter(state,level);
  if(!Number.isInteger(slot)||slot<0||slot>3||!canAssign(binding,state.profession,level,state.owned))return false;
  if(source!==undefined){
    if(!Number.isInteger(source)||source<0||source>3||!binding||JSON.stringify(state.shortcuts[source])!==JSON.stringify(binding))return false;
    state.shortcuts[source]=state.shortcuts[slot]??null;
  }
  state.shortcuts[slot]=binding;
  state.equippedSkills=state.shortcuts.flatMap(b=>b?.kind==='skill'?[b.id]:[]);
  return true;
}
export function setShortcutKeys(state:CharacterState,keys:string[]):boolean{
  if(!validShortcutKeys(keys))return false;state.shortcutKeys=[...keys];return true;
}
export function allocate(state:CharacterState,level:number,attribute:keyof CharacterState['attributes']):boolean{
  if(attributePoints(state,level)<1)return false;state.attributes[attribute]++;return true;
}
export function rankUp(state:CharacterState,level:number,slot:number):boolean{
  if(slot<0||slot>3||skillPoints(state,level)<1||state.ranks[slot]!>=5)return false;state.ranks[slot]!++;return true;
}
export function unequip(state:CharacterState,id:string):boolean{
  const gear=GEAR.find(g=>g.id===id);if(!gear||!canUnequip(state.equipment,gear.slot,id))return false;
  state.equipment.charm='';return true;
}
export function equip(state:CharacterState,id:string):boolean{
  const gear=GEAR.find(g=>g.id===id);
  if(!gear||!state.owned.includes(id)||!gear.classes.includes(state.profession))return false;
  state.equipment[gear.slot]=id;
  if(gear.slot==='outfit'&&['indigo','linen'].includes(id))state.appearance.outfit=id as Appearance['outfit'];
  return true;
}
export function characterStats(state:CharacterState,level:number):DerivedStats{
  const stats=derivedStats(level);
  stats.atk+=state.attributes.power*2;stats.maxHp+=state.attributes.vitality*10;stats.defense+=state.attributes.guard;
  for(const id of Object.values(state.equipment)){
    const gear=GEAR.find(g=>g.id===id);
    if(gear){stats.atk+=gear.atk;stats.maxHp+=gear.hp;stats.defense+=gear.def;}
  }
  return stats;
}
function copySavedInto(fresh:CharacterState,saved:Partial<CharacterState>):void{
  if(validAppearance(saved.appearance)){fresh.appearance=structuredClone(saved.appearance);fresh.created=saved.created===true;}
  if(saved.classVersion===2&&typeof saved.profession==='string'&&(['villager','sword-soldier','spear-soldier','forest-hunter','shaman'] as string[]).includes(saved.profession)){
    fresh.profession=saved.profession as ClassId;
  }
  for(const a of ['power','vitality','guard'] as const){
    const n=saved.attributes?.[a];fresh.attributes[a]=Number.isFinite(n)?Math.min(87,Math.max(0,Math.trunc(n!))):0;
  }
  if(Array.isArray(saved.ranks)&&saved.ranks.length===4)fresh.ranks=saved.ranks.map(n=>Number.isFinite(n)?Math.min(5,Math.max(0,Math.trunc(n))):0);
  fresh.owned=[...new Set(['bamboo-knife','indigo','linen','talisman',...(Array.isArray(saved.owned)?saved.owned.filter(id=>GEAR.some(g=>g.id===id)):[])])];
  fresh.potions=Number.isFinite(saved.potions)?Math.max(0,Math.min(99,Math.trunc(saved.potions!))):3;
  fresh.advancementQuest=saved.advancementQuest===true;
  if(saved.shortcuts!==undefined)fresh.shortcuts=normalizeShortcutSlots(saved.shortcuts);
  if(Array.isArray(saved.shortcutKeys))setShortcutKeys(fresh,saved.shortcutKeys);
  for(const id of Object.values(saved.equipment??{}))if(typeof id==='string')equip(fresh,id);
  for(const [id,n] of Object.entries(saved.learned??{}))if(Number.isFinite(n))fresh.learned[id]=Math.min(5,Math.max(0,Math.trunc(n)));
}
export function loadCharacter():CharacterState{
  const fresh=newCharacter();
  try{
    const saved=JSON.parse(localStorage.getItem('phi-character-v1')??'null') as Partial<CharacterState>|null;
    if(saved&&saved.attributes&&saved.equipment)copySavedInto(fresh,saved);
  }catch{/* New character if browser storage is unavailable or corrupt. */}
  return fresh;
}
